import type { Ref } from "react";

import { cx } from "@/lib/cx";
import { tapHalo } from "./tones";

/**
 * The iOS toggle. The app had none: every boolean was drawn as a checkbox, and
 * a checkbox is the wrong shape for a setting.
 *
 * A checkbox says "this is one of several values you are *composing*, and it
 * takes effect when you submit". A switch says "this is a state, and flipping
 * it changes the world now". Kobo's booleans — notifications on, autostart on,
 * a routine being the active one — are all the second kind, and they were all
 * wearing the first kind's clothes.
 *
 * **A switch is only correct where the write is immediate.** If the value needs
 * a Save button next to it, it is a form field and it is a checkbox. That rule
 * is the whole reason this component exists and it is not a prop.
 *
 * ---------------------------------------------------------------------------
 * Geometry and motion (`IOS-DESIGN.md` §5.2)
 * ---------------------------------------------------------------------------
 * The compact track is 40×24 with a 16px thumb and 16px of travel. A coarse
 * pointer uses a 48×32 track with a 24px thumb and receives an invisible tap
 * halo, so density never costs the finger its target. Off is `bg-fill-1`, on
 * is the vivid `bg-accent` — a saturation change hover cannot imitate.
 *
 * **The track colour changes instantly and only the thumb travels.** The track
 * is carrying a selection, and `UI-REVIEW.md` P6 is about exactly this: a
 * crossfaded selection renders two states at once. The thumb is not a
 * selection, it is an object moving in space, so it gets the spring
 * (`dur-snappy` + `ease-spring-snappy`, the pair those 320ms were solved for).
 * `transition-transform` names the one property that may animate, so the
 * background colour cannot be swept in by accident.
 *
 * The white thumb is the one place the app uses a white knob. On the vivid
 * accent it is 3.75:1 and on the off-track it relies on `shadow-knob`, exactly
 * as iOS does — and unlike the segmented-control knob that `IOS-DESIGN.md` §5.3
 * rejects, this knob is 27px of solid geometry rather than a 13px label's
 * backing, so its edge is legible without carrying any text.
 */

export type SwitchProps = {
  checked: boolean;
  /** Called with the value it should become. Write immediately — see above. */
  onChange: (next: boolean) => void;
  /**
   * The accessible name. Omit it only when the switch is wired to a visible
   * label with `labelledBy` — an unnamed switch announces as "switch, on".
   */
  label?: string;
  /** The id of a visible label, e.g. the title of the grouped row it sits in. */
  labelledBy?: string;
  /** Points at the explanatory line under the row, when there is one. */
  describedBy?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
  ref?: Ref<HTMLButtonElement>;
};

export function Switch({
  checked,
  onChange,
  label,
  labelledBy,
  describedBy,
  disabled = false,
  id,
  className,
  ref,
}: SwitchProps) {
  return (
    <button
      ref={ref}
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cx(
        "busee-switch relative inline-flex h-6 w-10 shrink-0 items-center rounded-control border-2 border-ps1-orange p-0.5",
        "pointer-coarse:h-8 pointer-coarse:w-12",
        tapHalo,
        // No transition on the track: this is the selection channel.
        "transition-none select-none",
        checked ? "border-[#ffe08a] bg-ps1-gold-fill" : "bg-ps1-panel",
        "disabled:pointer-events-none disabled:opacity-40",
        className,
      )}
    >
      <span
        aria-hidden
        className={cx(
          "block size-4 rounded-control bg-ps1-text pointer-coarse:size-6",
          "transition-transform dur-snappy",
          checked ? "translate-x-4" : "translate-x-0",
        )}
      />
    </button>
  );
}
